import {
  Box,
  Divider,
  Text,
  Flex,
  Select,
  TextInput,
  Button,
  Radio,
  Group,
} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type ApprovalBoxProps = {
  ruleId: string;
  authority: string;
  approver: string;
  authMode: string;
  onAuthModeChange: (mode: string) => void;
};

export default function ApprovalBox({
  ruleId,
  authority,
  approver,
  authMode: authModeProp,
  onAuthModeChange,
}: ApprovalBoxProps) {
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState(authModeProp);
  const [otpType, setOtpType] = useState<"SmartOTP.Push" | "SmartOTP.Code">(
    "SmartOTP.Push"
  );
  // Đồng bộ state với props khi props thay đổi
  useEffect(() => {
    setAuthMode(authModeProp);
  }, [authModeProp]);

  return (
    <Box
      sx={{
        width: "420px",
        backgroundColor: "#FDFDFD",
        padding: "20px",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "fit-content",
      }}
    >
      <Box>
        <Text sx={{ color: "#131618", fontWeight: 600 }} mb={16} fz="lg">
          {t("APPROVAL")}
        </Text>
        <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Text sx={{ color: "#5E656B", fontWeight: 400 }}>
              {t("RULE_ID")}
            </Text>
            <Text sx={{ color: "#131618", fontWeight: 600 }}>{ruleId}</Text>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Text sx={{ color: "#5E656B", fontWeight: 400 }}>
              {t("AUTHORITY")}
            </Text>
            <Text sx={{ color: "#131618", fontWeight: 600 }}>{authority}</Text>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Text sx={{ color: "#5E656B", fontWeight: 400 }}>
              {t("APPROVER")}
            </Text>
            <Flex gap={5} align="center">
              <Text sx={{ color: "#0047D0", fontWeight: 600 }}>{approver}</Text>
              <IconChevronDown size={18} color="#0047D0" />
            </Flex>
          </Box>
        </Box>
      </Box>

      <Divider variant="dashed" color="#D8DDE2" />
      <Box sx={{ display: "flex", gap: "16px", flexDirection: "column" }}>
        <Box sx={{ display: "flex", gap: "8px", flexDirection: "column" }}>
          <Text sx={{ color: "#131618", fontWeight: 600, fontSize: "16px" }}>
            {t("AUTHENTICATION_MODE")}
          </Text>
          <Select
            data={[
              { value: "SmartOTP", label: "Smart OTP" },
              { value: "SMSOTP", label: "SMS OTP" },
            ]}
            value={authMode}
            onChange={(value) => {
              const selected = value || "SmartOTP";
              setAuthMode(selected);
              onAuthModeChange(selected); // Callback để thông báo cho component cha
            }}
            radius="md"
            rightSection={
              <IconChevronDown size={18} stroke={1.8} color="#E1E4EA" />
            }
            sx={{
              ".mantine-Select-input": {
                backgroundColor: "#FDFDFD",
                borderColor: "#E1E4EA",
                color: "#131618",
                "&:hover": {
                  borderColor: "#1E6FA1",
                },
                "&:focus, &:focus-within": {
                  borderColor: "#2A81D0 !important",
                  boxShadow: "none",
                },
              },
              ".mantine-Select-item": {
                "&[data-hovered]": {
                  backgroundColor: "#D7E8FA",
                  color: "#1E6FA1",
                },
                "&[data-selected]": {
                  backgroundColor: "#2A81D0",
                  color: "#FFFFFF",
                },
              },
              "&[data-opened] .mantine-Select-input": {
                borderColor: "#2A81D0",
              },
            }}
          />
        </Box>

        {authMode === "SmartOTP" && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Text sx={{ color: "#131618", fontWeight: 600 }}>
                {t("MODE")}
              </Text>
              <Radio.Group
                value={otpType}
                onChange={(value) =>
                  setOtpType(value as "SmartOTP.Push" | "SmartOTP.Code")
                }
              >
                <Group>
                  <Radio
                    value="SmartOTP.Push"
                    label="SmartOTP Push"
                    styles={{
                      radio: {
                        borderColor: "#5E656B", // Viền mặc định
                        "&:checked": {
                          backgroundColor: "#2A81D0", // Màu nền khi chọn
                          borderColor: "#2A81D0", // Viền khi chọn
                        },
                      },
                      label: {
                        color:
                          otpType === "SmartOTP.Push" ? "#2A81D0" : "#131618", // Đổi màu chữ khi chọn
                      },
                    }}
                  />
                  <Radio
                    value="SmartOTP.Code"
                    label="SmartOTP Code"
                    styles={{
                      radio: {
                        borderColor: "#5E656B",
                        "&:checked": {
                          backgroundColor: "#2A81D0",
                          borderColor: "#2A81D0",
                        },
                      },
                      label: {
                        color:
                          otpType === "SmartOTP.Code" ? "#2A81D0" : "#131618",
                      },
                    }}
                  />
                </Group>
              </Radio.Group>
            </Box>
            {otpType === "SmartOTP.Code" && (
              <Flex gap="sm" align="flex-start">
                <TextInput
                  radius="md"
                  placeholder={t<string>("INPUT_YOUR_OTP")}
                  style={{ flex: 1 }}
                  styles={{ input: { height: 40 } }}
                />
                <Button
                  h={40}
                  type="submit"
                  sx={{
                    width: "104px",
                    background: "#E5F4FE",
                    borderRadius: "8px",
                    padding: "8px 10px",
                    cursor: "pointer",
                    fontWeight: 400,
                    color: "#2A81D0",
                    ":hover": {
                      background: "#E5F4FE",
                      color: "#2A81D0",
                    },
                  }}
                >
                  {t("GET_OTP")}
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
